/**
 * 分析アプリの「軸候補」共有ユーティリティ (docs/105 地図軸 parity、old2 `ecwConfig` 相当)。
 *
 * old2 では `ecwConfig.buildAvailableAxes/getAvailableAxes` が matrix / map / filter に
 * **同一の軸セット** (category-major(master色) / rating-scale グラデ / composite) を供給していた。
 * v2 は matrix (`AnalysisMatrixView`) が master / dataSettings / M5 custom 由来の豊富な軸を持つ一方、
 * map (`AnalysisMapView`) は `none/emotion/category/numeric` の 4 種に留まり parity が無かった。
 *
 * 本 util は「フル中央 builder の新設は避け contained に」という方針 (docs/105) に従い、
 * **地図の色分け / サイズ軸が必要とする最小限** の軸候補だけを供給する:
 *   - emotion: master colorMap 由来 (一覧 / matrix / 詳細 chip と同色)
 *   - category 大分類 (major): master colorMap 由来 (matrix の `category.major` と同色) ← parity 追加
 *   - category 詳細 (categoryCd): row.color 由来 (matrix の `category.categoryCd` と同色)
 *   - rating-scale postField: scale 値で正負グラデ (matrix の `postField:` rating-scale 軸と同じ正負配色)
 *   - 数値 postField / extraFields: 正負 0 の 3 bucket (旧 EcwMapView rating-scale 軸 簡素版)
 *
 * 色解決は Phase I-1 で入れた resolver (`resolveChipColor` + master colorMap) を流用する。
 * 実際の `ColorRule` 組み立て (bucket 化) は呼び出し側 (AnalysisMapView) が
 * `axis.kind` + `axis` の付随情報を見て行う (この util は「候補列挙 + 色解決の materials」を返す)。
 */
import {
  MODERN_COLOR_PALETTE,
  type AiCategoryMaster,
  type AiOutputFormat,
  type DistrictMode,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { resolveChipColor, type AiCategoryMastersResult, type PostMinimal } from '../posts';
import { expandPostToClassificationRows } from './classificationAdapter';

/** 地図の color / size 軸として選べる軸の種別。 */
export type MapAxisKind =
  | 'none'
  // ECW 感情 (master colorMap 由来色、配列先頭を代表値)
  | 'emotion'
  // 対象カテゴリ 大分類 (major, master colorMap 由来色) ← old2 parity
  | 'category-major'
  // 任意 mode の M3 分類軸の大分類 (master colorMap 色、代表=rank最小)。ECW 専用の
  // category-major を keikan/custom 含む任意 mode に一般化したもの。
  | 'classification-major'
  // 対象カテゴリ 詳細 (categoryCd, row.color 由来色)
  | 'category'
  // rating-scale postField (scale 値で正負グラデ)
  | 'rating-scale'
  // select / multiselect postField (選択肢ごとに value bucket、palette 色)
  | 'select-value'
  // 任意の数値 (postField / extraFields)、正負 0 の 3 bucket
  | 'numeric'
  // docs/29 §8-1: 追加の AI 解析の観点の軸 (key = analysis:<観点>|<元の key>)。マトリクスと同じグループの色。
  | 'perspective-axis'
  // 管理画面で地区に設定した色分けルール (District.mapDisplay.colorRule) をそのまま使う。
  // 投稿アプリの地図・一覧・詳細と同じ色になる。1 軸 (value) でも 2 軸 (quadrant) でも可で、
  // 軸名・式・色はすべて管理画面側の設定なので、ここでは中身を解釈しない。
  | 'district-rule';

export interface MapAxisOption {
  /** select value。numeric/rating-scale は対象 key、それ以外は kind 名と同じ固定 key。 */
  key: string;
  label: string;
  kind: MapAxisKind;
  /** rating-scale 軸のときの FieldDef (scale 範囲 / labels の解決用)。 */
  field?: FieldDef;
  /** classification-major 軸のときの M3 軸 key (例 'aspect' / 'object')。代表 majorCode 抽出用。 */
  classificationAxisKey?: string;
  /** classification-major 軸のときの色解決用 master (colorMap / row.color)。 */
  master?: AiCategoryMaster;
}

/**
 * 管理画面で地区に設定した色分け (District.mapDisplay.colorRule) を使う軸。
 * 投稿アプリの地図と同じ色になるので、参加者が見ている色のまま分析できる。
 * key は M5 `defaultColorAxis` から名指しできるよう固定文字列にする。
 */
export const DISTRICT_RULE_AXIS: MapAxisOption = {
  key: 'district-rule',
  label: '地区の色分け設定 (投稿アプリと同じ)',
  kind: 'district-rule',
};

/** ECW 感情/分類が固定 path で取り出せる派生軸 (旧 EcwMapView の category-* 軸に相当)。 */
export const EMOTION_AXIS: MapAxisOption = { key: 'emotion', label: '感情', kind: 'emotion' };
export const CATEGORY_MAJOR_AXIS: MapAxisOption = {
  key: 'category-major',
  label: '対象 (大分類)',
  kind: 'category-major',
};
export const CATEGORY_AXIS: MapAxisOption = {
  key: 'category',
  label: '対象 (詳細)',
  kind: 'category',
};

/** numeric とみなす値か (rating-scale / numeric 軸候補の判定)。 */
function isNumericValue(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string' && v.trim() !== '') return Number.isFinite(Number(v));
  return false;
}

/** posts を走査して数値 extraFields key を収集 (postFields で型解決できない素の数値軸候補)。 */
export function collectNumericFieldKeys(posts: PostMinimal[]): string[] {
  const keys = new Set<string>();
  for (const p of posts) {
    const extra = p.extraFields;
    if (!extra || typeof extra !== 'object') continue;
    for (const [k, v] of Object.entries(extra)) {
      if (isNumericValue(v)) keys.add(k);
    }
  }
  return [...keys].sort();
}

/** posts に ECW 感情 / 分類が 1 件でもあるか。 */
function hasEcwField(posts: PostMinimal[], field: 'emotions' | 'categoryCodes'): boolean {
  return posts.some((p) => (p.aiAnalysisEcw?.[field]?.length ?? 0) > 0);
}

/**
 * dataSettings.postFields から「色 / サイズ軸に使える postField」を分類する。
 *   - rating-scale → color (グラデ) と size の両方に使える
 *   - number → color (正負) と size の両方に使える
 * `useAsAnalysisAxis === false` は admin が opt-out した軸として除外。
 */
function classifyPostFields(postFields: FieldDef[] | undefined): {
  ratingScale: FieldDef[];
  numbers: FieldDef[];
  selects: FieldDef[];
} {
  const ratingScale: FieldDef[] = [];
  const numbers: FieldDef[] = [];
  const selects: FieldDef[] = [];
  for (const f of postFields ?? []) {
    if (f.useAsAnalysisAxis === false) continue;
    if (f.type === 'rating-scale') ratingScale.push(f);
    else if (f.type === 'number') numbers.push(f);
    else if (f.type === 'select' || f.type === 'multiselect') selects.push(f);
  }
  return { ratingScale, numbers, selects };
}

/**
 * select / multiselect postField の出現値を頻度順で収集 (色 bucket 列挙用)。
 * multiselect (extraFields[key] が配列) は各要素を、単一 select は値そのものを数える。
 */
export function collectSelectValues(posts: PostMinimal[], fieldKey: string): string[] {
  const counts = new Map<string, number>();
  for (const p of posts) {
    const v = p.extraFields?.[fieldKey];
    const vals = Array.isArray(v) ? v : v != null && v !== '' ? [v] : [];
    for (const item of vals) {
      const s = String(item);
      if (s) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([val]) => val);
}

/**
 * 地図の **色分け (colorAxis)** 候補一覧を matrix と同水準で組み立てる。
 *
 * - emotion / category-major / category は posts に該当データがある場合のみ追加
 * - rating-scale postField は dataSettings 由来 (matrix の `postField:` 軸と同集合)
 * - numeric は number 型 postField + 素の数値 extraFields (postField で型付かないもの)
 *
 * @param noneOption 先頭に置く「色なし」option (AnalysisMapView が自前ラベルで渡す)。
 * @param hasDistrictRule 地区に色分け設定 (District.mapDisplay.colorRule) があるか。
 *   true のとき「色なし」の直後に {@link DISTRICT_RULE_AXIS} を置く (管理画面の設定を最優先で見せる)。
 */
export function buildColorAxisOptions(
  posts: PostMinimal[],
  postFields: FieldDef[] | undefined,
  noneOption: MapAxisOption,
  classificationMajorAxes: MapAxisOption[] = [],
  resolver?: AiCategoryMastersResult,
  hasDistrictRule = false,
): MapAxisOption[] {
  const opts: MapAxisOption[] = [noneOption];
  // 管理画面で設定された色分けは「投稿アプリと同じ見え方」なので候補の先頭に置く。
  if (hasDistrictRule) opts.push(DISTRICT_RULE_AXIS);
  // 要件7: ECW 専用の「感情 / 対象」軸は、対応マスタ (emotionMaster / categoryMaster) が
  // **この地区にロードされているときだけ** 出す。matrix (deriveAxisOptionsFromMasters) と同じく
  // master 駆動にすることで、別モード地区 (例: 箱根 keikan) の投稿に残る legacy ECW データで
  // 「感情」軸が誤って出てしまう (ハードコード混入) のを防ぐ。任意 mode の分類軸は下の
  // classificationMajorAxes (M3 outputFormat 駆動) が供給する。
  const hasMaster = (templateVar: string) => resolver?.byTemplateVar.has(templateVar) ?? false;
  if (hasMaster('emotionMaster') && hasEcwField(posts, 'emotions')) opts.push(EMOTION_AXIS);
  if (hasMaster('categoryMaster') && hasEcwField(posts, 'categoryCodes')) {
    opts.push(CATEGORY_MAJOR_AXIS, CATEGORY_AXIS);
  }
  // 任意 mode の M3 分類軸 大分類 (keikan の景観の観点 / 対象物 等)。ECW 由来軸の直後に並べ、
  // 分類モードでは既定の色軸候補が「意味のある大分類」になるようにする。
  for (const a of classificationMajorAxes) opts.push(a);
  const { ratingScale, numbers, selects } = classifyPostFields(postFields);
  // rating-scale 軸 (グラデ): key は素の field.key (postField: prefix は付けない、地図 select 内で完結)。
  const usedKeys = new Set<string>();
  for (const f of ratingScale) {
    opts.push({ key: f.key, label: f.label || f.key, kind: 'rating-scale', field: f });
    usedKeys.add(f.key);
  }
  // select / multiselect 型 postField (選択肢ごとの value 色分け)。custom mode (例 keikan) の
  // KeikanType 等を地図色軸にできるようにする (従来は数値軸しか出ず色分け不能だった)。
  for (const f of selects) {
    if (usedKeys.has(f.key)) continue;
    opts.push({ key: f.key, label: f.label || f.key, kind: 'select-value', field: f });
    usedKeys.add(f.key);
  }
  // number 型 postField (正負軸)。
  for (const f of numbers) {
    if (usedKeys.has(f.key)) continue;
    opts.push({ key: f.key, label: f.label || f.key, kind: 'numeric', field: f });
    usedKeys.add(f.key);
  }
  // postFields で型解決できない素の数値 extraFields (後方互換: 旧 collectNumericFieldKeys 経路)。
  for (const k of collectNumericFieldKeys(posts)) {
    if (usedKeys.has(k)) continue;
    opts.push({ key: k, label: k, kind: 'numeric' });
    usedKeys.add(k);
  }
  return opts;
}

/**
 * 地図の **サイズ (sizeAxis)** 候補一覧。サイズは連続値が必要なので rating-scale / number /
 * 数値 extraFields のみ (emotion / category は対象外)。
 */
export function buildSizeAxisOptions(
  posts: PostMinimal[],
  postFields: FieldDef[] | undefined,
  noneOption: MapAxisOption,
): MapAxisOption[] {
  const opts: MapAxisOption[] = [noneOption];
  const { ratingScale, numbers } = classifyPostFields(postFields);
  const usedKeys = new Set<string>();
  for (const f of [...ratingScale, ...numbers]) {
    if (usedKeys.has(f.key)) continue;
    opts.push({ key: f.key, label: f.label || f.key, kind: 'numeric', field: f });
    usedKeys.add(f.key);
  }
  for (const k of collectNumericFieldKeys(posts)) {
    if (usedKeys.has(k)) continue;
    opts.push({ key: k, label: k, kind: 'numeric' });
    usedKeys.add(k);
  }
  return opts;
}

/**
 * 感情 / 分類詳細 軸で出現する値 (代表 = 配列先頭) を頻度順で収集。
 * field='emotions' → emotion label、'categoryCodes' → categoryCd。
 */
export function collectEcwValues(
  posts: PostMinimal[],
  field: 'emotions' | 'categoryCodes',
): string[] {
  const counts = new Map<string, number>();
  for (const p of posts) {
    const first = p.aiAnalysisEcw?.[field]?.[0];
    if (!first) continue;
    counts.set(first, (counts.get(first) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v);
}

/**
 * 対象カテゴリ 大分類 (major) の出現値 (majorCd) を頻度順で収集 (categoryCd → master row → majorCd)。
 * matrix の `category.major` 集計と同じ「categoryCd を master の majorCd に寄せる」経路。
 */
export function collectCategoryMajorValues(
  posts: PostMinimal[],
  resolver: AiCategoryMastersResult,
): string[] {
  const categoryMaster = resolver.byTemplateVar.get('categoryMaster');
  const counts = new Map<string, number>();
  for (const p of posts) {
    const codes = p.aiAnalysisEcw?.categoryCodes ?? [];
    // 代表 = 配列先頭の majorCd (色分けは 1 ピン 1 色のため先頭採用、旧 EcwMapView と同方針)。
    const first = codes[0];
    if (!first) continue;
    const row = lookupRowByCategoryCd(resolver, categoryMaster, first);
    const major = row?.majorCd;
    if (!major) continue;
    counts.set(major, (counts.get(major) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v);
}

/** categoryMaster 限定で categoryCd → row を引く (複数 master 混在時の取り違え防止)。 */
function lookupRowByCategoryCd(
  resolver: AiCategoryMastersResult,
  categoryMaster: AiCategoryMaster | undefined,
  categoryCd: string,
) {
  if (categoryMaster) {
    for (const row of categoryMaster.rows ?? []) {
      // AI 出力コードは結合形 (例 "D-01")。bare categoryCd ("01") と結合形の両方で照合する。
      if (
        row.categoryCd === categoryCd ||
        `${row.majorCd ?? ''}-${row.categoryCd}` === categoryCd
      ) {
        return row;
      }
    }
  }
  // master 限定で見つからなければ全 master 横断 fallback (resolver.byCategoryCd は結合キーも登録済)。
  return resolver.byCategoryCd.get(categoryCd);
}

/** emotion label → master colorMap 由来色 (一覧 / matrix / 詳細の EmotionChips と同経路)。 */
export function resolveEmotionColor(
  resolver: AiCategoryMastersResult,
  label: string,
): string | null {
  const master = resolver.byTemplateVar.get('emotionMaster');
  const row = resolver.byCategoryLabel.get(label);
  return row ? resolveChipColor(row, master) : null;
}

/** categoryCd → master colorMap / row.color 由来色 (一覧 / matrix / 詳細の CategoryChips と同経路)。 */
export function resolveCategoryColor(
  resolver: AiCategoryMastersResult,
  categoryCd: string,
): string | null {
  const master = resolver.byTemplateVar.get('categoryMaster');
  const row = lookupRowByCategoryCd(resolver, master, categoryCd);
  return row ? resolveChipColor(row, master) : null;
}

/** majorCd → master colorMap 由来色 (matrix の category.major 軸ヘッダと同色)。 */
export function resolveCategoryMajorColor(
  resolver: AiCategoryMastersResult,
  majorCd: string,
): string | null {
  const master = resolver.byTemplateVar.get('categoryMaster');
  if (!master) return null;
  // colorAxis が major/majorCd のとき colorMap[majorCd] (もしくは major 名)。
  const byCd = master.colorMap?.[majorCd];
  if (byCd) return byCd;
  // majorCd → major 名 → colorMap[major 名] の経路も試す (colorAxis='major' 設定の master)。
  for (const row of master.rows ?? []) {
    if (row.majorCd === majorCd) {
      const byMajor = row.major ? master.colorMap?.[row.major] : undefined;
      if (byMajor) return byMajor;
      return resolveChipColor(row, master);
    }
  }
  return null;
}

/** majorCd → major 表示名 (凡例ラベル用、見つからなければ majorCd をそのまま)。 */
export function resolveCategoryMajorLabel(
  resolver: AiCategoryMastersResult,
  majorCd: string,
): string {
  const master = resolver.byTemplateVar.get('categoryMaster');
  for (const row of master?.rows ?? []) {
    if (row.majorCd === majorCd && row.major) return row.major;
  }
  return majorCd;
}

/* ===== generic 分類大分類 軸 (任意 mode、ECW 専用 category-major の一般化) ===== */

/** M3 分類軸定義 → 色解決用 master を解決 (masterId 直引き → templateVar fallback)。 */
function resolveAxisMaster(
  resolver: AiCategoryMastersResult,
  axisDef: { masterId?: string; templateVar?: string; key?: string },
): AiCategoryMaster | undefined {
  if (axisDef.masterId) {
    const byId = resolver.masters.find((m) => m.id === axisDef.masterId);
    if (byId) return byId;
  }
  if (axisDef.templateVar) {
    const byVar = resolver.byTemplateVar.get(axisDef.templateVar);
    if (byVar) return byVar;
  }
  return undefined;
}

/**
 * M3 (outputFormat) の classificationAxes から、master が解決できる軸ごとに地図の
 * 「大分類」色軸候補を生成する。ECW 専用だった category-major を keikan/custom 含む任意 mode に
 * 一般化したもの (代表 = rank 最小の分類の majorCode を master colorMap 色で塗る)。
 */
export function buildClassificationMajorAxes(
  format: AiOutputFormat | null | undefined,
  resolver: AiCategoryMastersResult | undefined,
): MapAxisOption[] {
  const axes = format?.schema?.classificationAxes;
  if (!axes || !resolver) return [];
  const opts: MapAxisOption[] = [];
  for (const axisDef of axes) {
    const key = (axisDef as { key?: string }).key ?? axisDef.codeField;
    const master = resolveAxisMaster(
      resolver,
      axisDef as { masterId?: string; templateVar?: string },
    );
    if (!master || !(master.rows?.length ?? 0)) continue;
    opts.push({
      key: `clsmajor:${key}`,
      label: `${axisDef.label || key} (大分類)`,
      kind: 'classification-major',
      classificationAxisKey: key,
      master,
    });
  }
  return opts;
}

/**
 * 投稿の代表 (rank 最小) 分類の、指定軸の majorCode を返す (地図ピン色 bucket 用)。
 * matrix と同じ `expandPostToClassificationRows` を使うので mode (generic/bousai/walker) を問わない。
 * 分類が無い / 当該軸に大分類が無い場合は ''。
 */
export function resolveRepresentativeMajor(
  post: PostMinimal,
  format: AiOutputFormat | null | undefined,
  mode: DistrictMode | null | undefined,
  axisKey: string,
): string {
  const rows = expandPostToClassificationRows(post, format, mode ?? undefined);
  let best: (typeof rows)[number] | null = null;
  for (const r of rows) {
    if (best === null || r.rank < best.rank) best = r;
  }
  return best ? (best.axes[axisKey]?.majorCode ?? '') : '';
}

/** posts の代表 major を頻度順に集計 (凡例 bucket 列挙用)。 */
export function collectRepresentativeMajorValues(
  posts: PostMinimal[],
  format: AiOutputFormat | null | undefined,
  mode: DistrictMode | null | undefined,
  axisKey: string,
): string[] {
  const counts = new Map<string, number>();
  for (const p of posts) {
    const major = resolveRepresentativeMajor(p, format, mode, axisKey);
    if (!major) continue;
    counts.set(major, (counts.get(major) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([v]) => v);
}

/** 任意 master の majorCd → colorMap 由来色 (resolveCategoryMajorColor の master 一般化版)。 */
export function resolveMasterMajorColor(master: AiCategoryMaster, majorCd: string): string | null {
  const byCd = master.colorMap?.[majorCd];
  if (byCd) return byCd;
  for (const row of master.rows ?? []) {
    if (row.majorCd === majorCd) {
      const byMajor = row.major ? master.colorMap?.[row.major] : undefined;
      if (byMajor) return byMajor;
      return resolveChipColor(row, master);
    }
  }
  return null;
}

/** 任意 master の majorCd → major 表示名 (凡例ラベル用、見つからなければ majorCd)。 */
export function resolveMasterMajorLabel(master: AiCategoryMaster, majorCd: string): string {
  for (const row of master.rows ?? []) {
    if (row.majorCd === majorCd && row.major) return row.major;
  }
  return majorCd;
}

/** palette fallback 色 (master 未解決値用)。index で巡回。 */
export function paletteColor(index: number): string {
  return MODERN_COLOR_PALETTE[index % MODERN_COLOR_PALETTE.length] ?? '#9CA3AF';
}
